/**
 * Sugerencia de typos en el dominio del email ("¿Quisiste decir gmail.com?").
 *
 * Es la capa anti-typo del frontend: el server valida que el dominio exista
 * (MX/A), pero los typos populares (`gmial.com`, `gamil.com`) son dominios
 * typosquatteados CON correo activo — el DNS no los ve. Acá los detectamos
 * por distancia de edición contra el top de dominios reales del padrón y
 * SUGERIMOS la corrección con confirmación del usuario (nunca autocorregir:
 * un dominio raro puede ser legítimo).
 */

/**
 * Top de dominios reales del padrón (corrida 04/06/2026 sobre 191.427
 * emails), en orden de frecuencia — ante empate de distancia gana el más
 * común. Incluye `ymail.com` (Yahoo legítimo a distancia 1 de gmail) y
 * `mail.com` para que la whitelist los proteja: si el dominio tipeado YA es
 * conocido, jamás se sugiere otro.
 */
export const DOMINIOS_FRECUENTES: readonly string[] = [
  'gmail.com',
  'gna.gob.ar',
  'hotmail.com',
  'yahoo.com.ar',
  'hotmail.com.ar',
  'outlook.com',
  'yahoo.com',
  'live.com.ar',
  'live.com',
  'outlook.es',
  'hotmail.es',
  'outlook.com.ar',
  'icloud.com',
  'iosfa.gob.ar',
  'mail.com',
  'msn.com',
  'yahoo.es',
  'ymail.com',
  'arnet.com.ar',
  'fibertel.com.ar',
  'speedy.com.ar',
  'prefecturanaval.gov.ar',
  'medicus.com.ar',
];

/**
 * Typos de alto volumen que la distancia de edición no alcanza (umbral 2).
 * `gmail.com.ar` está a distancia 3 de gmail.com pero hay 319 cargados en el
 * padrón — Google no opera casillas .com.ar.
 */
const ALIAS_TYPO: Readonly<Record<string, string>> = {
  'gmail.com.ar': 'gmail.com',
};

/**
 * Distancia Damerau-Levenshtein (con transposición): el typo más común es
 * invertir dos letras (`gmial`, `hotmial`) y Levenshtein simple lo cuenta
 * como 2 ediciones; Damerau como 1, que es lo que es.
 */
function damerau(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + costo);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
}

/**
 * Si el dominio del email parece un typo de un dominio frecuente, devuelve el
 * email corregido completo (`juan@gmial.com` → `juan@gmail.com`); si no, null.
 *
 * Reglas:
 *  - Whitelist-first: un dominio frecuente nunca genera sugerencia (protege
 *    ymail.com vs gmail.com, hotmail.es vs hotmail.com).
 *  - Umbral relativo al largo: dominios cortos toleran 1 edición; largos, 2.
 *  - Solo sugiere con el email en formato completo (local@dominio.tld) para
 *    no molestar mientras se tipea.
 */
export function sugerirEmail(email: string): string | null {
  const limpio = (email ?? '').trim().toLowerCase();
  const at = limpio.indexOf('@');
  if (at <= 0 || at !== limpio.lastIndexOf('@')) return null;
  const local = limpio.slice(0, at);
  const dominio = limpio.slice(at + 1);
  const punto = dominio.lastIndexOf('.');
  if (punto <= 0 || punto === dominio.length - 1) return null; // sin TLD aún

  if (DOMINIOS_FRECUENTES.includes(dominio)) return null;

  const alias = ALIAS_TYPO[dominio];
  if (alias) return `${local}@${alias}`;

  const umbral = dominio.length <= 5 ? 1 : 2;
  let mejor: string | null = null;
  let mejorDist = umbral + 1;
  for (const candidato of DOMINIOS_FRECUENTES) {
    const dist = damerau(dominio, candidato);
    if (dist < mejorDist) {
      mejorDist = dist;
      mejor = candidato;
    }
  }
  return mejor ? `${local}@${mejor}` : null;
}
